import React, { useState, useEffect, useRef, memo } from "react";
import {
  Github,
  Linkedin,
  Mail,
  FileText,
  ExternalLink,
} from "lucide-react";

// Memoized Components
const StatusBadge = memo(() => (
  <div
    className="inline-block animate-float lg:mx-0"
  >
    <div className="inline-flex items-center space-x-2 bg-surface-light dark:bg-surface-dark border border-border-light dark:border-border-dark px-3 py-1.5 rounded text-xs font-mono text-text-secondary-light dark:text-text-secondary-dark">
      <span className="w-2 h-2 bg-primary rounded-full animate-pulse"></span>
      <span>Ready to Innovate</span>
    </div>
  </div>
));

const DOMAINS = [
  "Software Engineering",
  "Machine Learning",
  "Natural Language Processing",
  "Computer Vision",
  "Data Science",
];

const DOMAIN_DURATION = 2400; // ms each domain is visible
const ANIM_DURATION = 500;    // ms slide in/out

const MainTitle = memo(() => {
  const [index, setIndex] = useState(0);
  const [phase, setPhase] = useState("in"); // "in" | "hold" | "out"
  const timerRef = useRef(null);

  useEffect(() => {
    const runCycle = () => {
      // slide in
      setPhase("in");
      timerRef.current = setTimeout(() => {
        // hold
        setPhase("hold");
        timerRef.current = setTimeout(() => {
          // slide out
          setPhase("out");
          timerRef.current = setTimeout(() => {
            // next word
            setIndex((prev) => (prev + 1) % DOMAINS.length);
          }, ANIM_DURATION);
        }, DOMAIN_DURATION);
      }, ANIM_DURATION);
    };

    runCycle();
    return () => clearTimeout(timerRef.current);
  }, [index]);

  const animStyle = {
    display: "inline-block",
    transition: `opacity ${ANIM_DURATION}ms cubic-bezier(0.4,0,0.2,1), transform ${ANIM_DURATION}ms cubic-bezier(0.4,0,0.2,1)`,
    opacity: phase === "hold" ? 1 : 0,
    transform:
      phase === "in"
        ? "translateY(18px)"
        : phase === "out"
          ? "translateY(-18px)"
          : "translateY(0px)",
    willChange: "opacity, transform",
  };

  return (
    <div className="space-y-2">
      <h1 className="text-5xl sm:text-6xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.1] text-text-light dark:text-text-dark">
        Ifaz Ikram
      </h1>
      {/* Fixed height prevents layout shift between phrases */}
      <div
        className="overflow-hidden"
        style={{ minHeight: "3.5rem" }}
        aria-label={`Domain focus: ${DOMAINS[index]}`}
      >
        <p
          className="text-2xl sm:text-3xl md:text-3xl lg:text-3xl font-semibold tracking-tight text-primary flex items-center gap-2"
          style={animStyle}
        >
          <span className="text-text-secondary-light dark:text-text-secondary-dark font-mono text-lg sm:text-xl opacity-50 select-none"></span>
          {DOMAINS[index]}
        </p>
      </div>
    </div>
  );
});

const CTAButton = memo(({ href, text, icon: Icon, primary, target, rel }) => (
  <a
    href={href}
    target={target}
    rel={rel}
    className={`group relative inline-flex min-h-12 items-center justify-center px-8 py-3 font-medium rounded transition-all duration-300 ${primary
      ? "bg-primary hover:bg-blue-700 text-white"
      : "bg-transparent border border-border-light dark:border-border-dark text-text-light dark:text-text-dark hover:bg-surface-light dark:hover:bg-surface-dark hover:border-primary"
      }`}
  >
    <span>{text}</span>
    <Icon aria-hidden="true" className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
  </a>
));

const SocialLink = memo(({ icon: Icon, link, label, external = true }) => (
  <a
    href={link}
    aria-label={label}
    title={label}
    target={external ? "_blank" : undefined}
    rel={external ? "noopener noreferrer" : undefined}
    className="w-11 h-11 flex items-center justify-center border border-border-light dark:border-border-dark rounded text-text-secondary-light dark:text-text-secondary-dark hover:text-primary hover:border-primary bg-surface-light dark:bg-surface-dark transition-colors"
  >
    <Icon aria-hidden="true" className="w-5 h-5" />
  </a>
));

// Constants

const SOCIAL_LINKS = [
  { icon: Github, link: "https://github.com/Ifaz-Ikram", label: "Ifaz Ikram on GitHub" },
  { icon: Linkedin, link: "https://www.linkedin.com/in/ifaz-ikram", label: "Ifaz Ikram on LinkedIn" },
  { icon: Mail, link: "mailto:ifazi.23@cse.mrt.ac.lk", label: "Email Ifaz Ikram", external: false },
];

const Home = () => {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(true);
    return () => setIsLoaded(false);
  }, []);

  return (
    <div className="relative overflow-hidden font-sans" id="Home">
      {/* Navbar Spacer - Critical for proper layout */}
      <div className="h-16"></div>

      {/* Centered Content Container */}
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center">
        <div className="relative w-full">
          {/* Decorative Elements */}
          <div className="absolute right-0 top-1/4 w-1/3 h-1/2 border-l border-t border-border-light dark:border-border-dark opacity-50 pointer-events-none hidden lg:block"></div>
          <div className="absolute right-20 bottom-20 w-24 h-24 border border-dashed border-primary/30 pointer-events-none hidden lg:block"></div>

          <div
            className={`relative z-10 transition-all duration-1000 ${isLoaded ? "opacity-100" : "opacity-0"
              }`}
          >
            <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-8">
              <div className="w-full flex flex-col md:flex-row items-center md:justify-between gap-12 lg:gap-20">
                {/* Left Column */}
                <div
                  className="w-full md:w-1/2 space-y-6 sm:space-y-8 text-left md:text-left"
                >
                  <div className="space-y-4 sm:space-y-6">
                    <StatusBadge />
                    <MainTitle />

                    {/* University Info */}
                    <div className="flex flex-col gap-1 pl-3 border-l-2 border-primary/30">
                      <span className="text-base md:text-lg text-text-secondary-light dark:text-text-secondary-dark font-mono font-medium whitespace-nowrap">
                        Computer Science and Engineering Undergraduate
                      </span>
                      <span className="text-base md:text-lg text-text-secondary-light dark:text-text-secondary-dark font-mono font-medium opacity-60">
                        University of Moratuwa, Sri Lanka
                      </span>
                    </div>
                    {/* CTA Buttons */}
                    <div
                      className="flex flex-col sm:flex-row gap-4 w-full justify-start"
                    >
                      <CTAButton
                        href="#Portofolio"
                        text="View Projects"
                        icon={ExternalLink}
                        primary={true}
                      />
                      <CTAButton
                        href="/Ifaz_Ikram_CV.pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                        text="View CV"
                        icon={FileText}
                        primary={false}
                      />
                    </div>

                    {/* Social Links */}
                    <div
                      className="hidden sm:flex gap-4 justify-start"
                    >
                      {SOCIAL_LINKS.map((social, index) => (
                        <SocialLink key={index} {...social} />
                      ))}
                    </div>
                  </div>
                </div>

                {/* Lightweight decorative visual */}
                <div
                  className="hidden md:flex md:relative md:w-1/2 md:h-[500px] lg:h-[600px] xl:h-[750px] items-center justify-center z-0 md:z-auto opacity-30 md:opacity-100 pointer-events-none md:pointer-events-auto"
                >
                  <div className="relative w-80 h-80 lg:w-[28rem] lg:h-[28rem] border border-primary/30 rotate-45">
                    <div className="absolute inset-10 border border-dashed border-primary/40" />
                    <div className="absolute inset-20 bg-primary/10" />
                    <div className="absolute left-1/2 top-0 h-full w-px bg-primary/30" />
                    <div className="absolute top-1/2 left-0 h-px w-full bg-primary/30" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Decorative SVG */}
          <div className="hidden lg:block absolute right-10 bottom-10 opacity-10 dark:opacity-20 text-text-light dark:text-text-dark">
            <svg fill="none" height="200" viewBox="0 0 200 200" width="200" xmlns="http://www.w3.org/2000/svg">
              <circle cx="100" cy="100" r="99" stroke="currentColor" strokeWidth="1"></circle>
              <circle cx="100" cy="100" r="70" stroke="currentColor" strokeDasharray="4 4" strokeWidth="1"></circle>
              <path d="M100 0V200" stroke="currentColor" strokeWidth="1"></path>
              <path d="M0 100H200" stroke="currentColor" strokeWidth="1"></path>
              <rect height="30" stroke="currentColor" strokeWidth="1" width="30" x="85" y="85"></rect>
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(Home);
